// The single shared 2D-transform helper for the DXF export. Every coordinate the
// export emits flows through the affine helpers here — SVG user units → block-local
// units, node placement (objectFit: contain + rotation about the node center), and
// the screen→CAD Y-flip — so there is no ad-hoc geometry math per entity type.
//
// Matrices use the SVG convention: p' = (a*x + c*y + e, b*x + d*y + f).

export const IDENTITY = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

/** m ∘ n — apply n first, then m (matches SVG transform-list order `m n`). */
export const multiply = (m, n) => ({
  a: m.a * n.a + m.c * n.b,
  b: m.b * n.a + m.d * n.b,
  c: m.a * n.c + m.c * n.d,
  d: m.b * n.c + m.d * n.d,
  e: m.a * n.e + m.c * n.f + m.e,
  f: m.b * n.e + m.d * n.f + m.f,
});

export const applyMat = (m, p) => ({
  x: m.a * p.x + m.c * p.y + m.e,
  y: m.b * p.x + m.d * p.y + m.f,
});

export const translation = (tx, ty) => ({ a: 1, b: 0, c: 0, d: 1, e: tx, f: ty });

export const scaling = (sx, sy) => ({ a: sx, b: 0, c: 0, d: sy, e: 0, f: 0 });

export function rotationDeg(deg, cx = 0, cy = 0) {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const rot = { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 };
  if (!cx && !cy) return rot;
  return multiply(translation(cx, cy), multiply(rot, translation(-cx, -cy)));
}

const TRANSFORM_FN = /([a-zA-Z]+)\s*\(([^)]*)\)/g;

/** Parse an SVG `transform` attribute into a single matrix (identity if empty/invalid). */
export function parseSvgTransform(attr) {
  if (!attr) return IDENTITY;
  let result = IDENTITY;
  for (const match of String(attr).matchAll(TRANSFORM_FN)) {
    const fn = match[1];
    const args = (match[2].match(/-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g) || []).map(Number);
    let m = null;
    switch (fn) {
      case 'matrix':
        if (args.length === 6) m = { a: args[0], b: args[1], c: args[2], d: args[3], e: args[4], f: args[5] };
        break;
      case 'translate':
        m = translation(args[0] || 0, args[1] || 0);
        break;
      case 'scale':
        m = scaling(args[0] ?? 1, args[1] ?? args[0] ?? 1);
        break;
      case 'rotate':
        m = rotationDeg(args[0] || 0, args[1] || 0, args[2] || 0);
        break;
      case 'skewX':
        m = { a: 1, b: 0, c: Math.tan(((args[0] || 0) * Math.PI) / 180), d: 1, e: 0, f: 0 };
        break;
      case 'skewY':
        m = { a: 1, b: Math.tan(((args[0] || 0) * Math.PI) / 180), c: 0, d: 1, e: 0, f: 0 };
        break;
      default:
        m = null;
    }
    if (m) result = multiply(result, m);
  }
  return result;
}

/** xMidYMid "meet"/"contain" fit of an inner box into an outer box: uniform scale + centering. */
export function containFit(inner, outer) {
  const scale = Math.min(outer.width / inner.width, outer.height / inner.height);
  return {
    scale,
    dx: (outer.width - inner.width * scale) / 2,
    dy: (outer.height - inner.height * scale) / 2,
  };
}

/**
 * The uniform scale at which the canvas draws an SVG inside a node box.
 * `<img style="objectFit: contain">` first contain-fits the SVG's intrinsic size
 * (width/height attrs) into the box, then the viewBox "meet"s into that viewport.
 * Both steps are xMidYMid, so the viewBox center always lands on the box center
 * and only the combined uniform scale remains.
 */
export function symbolContainScale(viewBox, intrinsic, boxW, boxH) {
  const box = { width: boxW, height: boxH };
  if (intrinsic && intrinsic.width > 0 && intrinsic.height > 0) {
    const outerFit = containFit(intrinsic, box);
    const innerFit = containFit({ width: viewBox.width, height: viewBox.height }, intrinsic);
    return outerFit.scale * innerFit.scale;
  }
  return containFit({ width: viewBox.width, height: viewBox.height }, box).scale;
}

/**
 * SVG user units → block-local units: centered on the viewBox center, Y up.
 * Blocks are defined once per SVG in this space; per-node placement happens
 * entirely in the INSERT (position/scale/rotation).
 */
export function blockLocalMatrix(viewBox) {
  const cx = viewBox.minX + viewBox.width / 2;
  const cy = viewBox.minY + viewBox.height / 2;
  return multiply(scaling(1, -1), translation(-cx, -cy));
}

/** Screen/flow coordinates → CAD (math) coordinates: Y-flip about the page height. */
export const screenToCad = (p, pageHeight) => ({ x: p.x, y: pageHeight - p.y });

/**
 * INSERT parameters that reproduce the canvas rendering of a symbol node:
 * viewBox contain-fitted into the node box at node.position, rotated by
 * `angleDeg` (CSS convention, clockwise on screen) about the node center.
 */
export function symbolInsertPlacement(
  viewBox,
  intrinsic,
  nodeX,
  nodeY,
  nodeW,
  nodeH,
  angleDeg,
  pageHeight,
) {
  const center = screenToCad({ x: nodeX + nodeW / 2, y: nodeY + nodeH / 2 }, pageHeight);
  return {
    x: center.x,
    y: center.y,
    scale: symbolContainScale(viewBox, intrinsic, nodeW, nodeH),
    rotationDeg: -angleDeg,
  };
}
